import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogActions, Button, Box, IconButton, Divider, Typography } from "@mui/material";
import { zodResolver } from "@hookform/resolvers/zod";

import { CloseDialogButtonIcon } from "../../../../assets/icons/MUI/EventsIcons";

import { SharePostFormData } from "../../api/types/feed.types";
import { sharePostSchema } from "../../schemas/createPost.schema";
import { usePostShareMutation } from "../../api/mutations/usePostShareMutation";

import { TagsField } from "../basicPostFields/TagsField";
import { PrivacyField } from "../basicPostFields/PrivacyField";
import { DescriptionField } from "../basicPostFields/DescriptionField";
import { AchievementsField } from "../basicPostFields/AchievementsField";

interface SharePostModalProps {
  open: boolean;
  onClose: () => void;
  postId: number;
}

export const SharePostModal = ({ open, onClose, postId }: SharePostModalProps) => {
  const { t } = useTranslation("feed", { keyPrefix: "sharePost" });
  const { sharePost, isSharing } = usePostShareMutation({ postId });

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SharePostFormData>({
    resolver: zodResolver(sharePostSchema),
    defaultValues: {
      description: "",
      achievementId: null,
      tags: {
        old: [],
        new: [],
      },
      privacy: "PUBLIC",
      media: [],
      selectedUsers: [],
      selectedGroups: [],
    },
  });

  const onSubmit = async (data: SharePostFormData) => {
    sharePost(data);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "12px",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "24px 24px 20px 24px",
        }}
      >
        <Typography
          sx={{
            color: "var(--colors-text-text-primary-900, #101828)",
            fontFamily: "Montserrat",
            fontSize: "18px",
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "28px",
          }}
        >
          {t("sharePost")}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseDialogButtonIcon />
        </IconButton>
      </Box>
      <Divider sx={{ borderColor: "#EAECF0" }} />
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          <DescriptionField control={control} name="description" error={errors.description?.message} />
          <AchievementsField control={control} name="achievementId" error={errors.achievementId?.message} />
          <TagsField control={control} name="tags" error={errors.tags?.message} />
          <PrivacyField control={control} name="privacy" error={errors.privacy?.message} />
        </DialogContent>

        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button type="submit" variant="primary-md" disabled={isSubmitting || isSharing}>
            {t("share")}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default SharePostModal;
